/**
 * RiskAssessmentForm Component
 *
 * Full-screen form for creating and editing risk assessments.
 * Uses 5-step wizard progression following SeaReady's full-screen form pattern.
 *
 * @module components/RiskAssessmentForm
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Save,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

import { useSetBottomBarActions } from '../../../contexts/MobileBottomBarActionsContext';

import { ProgressIndicator } from './ProgressIndicator';
import { Step1_TaskDetails } from './Step1_TaskDetails';
import { Step2_HazardIdentification } from './Step2_HazardIdentification';
import { Step3_RiskScoring } from './Step3_RiskScoring';
import { Step4_ControlMeasures } from './Step4_ControlMeasures';
import { Step5_Review } from './Step5_Review';

// FT-116 REWRITE: All RAs now go to fleet storage - useRiskAssessments removed
import { useFleetRiskAssessments } from '../../../hooks/domain/fleet/useFleetRiskAssessments';
import { Hazard } from '../models/Hazard';

/**
 * Step configuration
 */
const STEPS = [
  { id: 1, label: 'Task Details', icon: '📋' },
  { id: 2, label: 'Hazards', icon: '⚠️' },
  { id: 3, label: 'Risk Score', icon: '📊' },
  { id: 4, label: 'Controls', icon: '🛡️' },
  { id: 5, label: 'Review', icon: '✅' }
];

/**
 * Initial form data structure
 */
const getInitialFormData = () => ({
  // Step 1: Task Details
  title: '',
  type: 'generic',
  description: '',
  selectedTask: null,
  location: '',
  department: '',
  procedureNotes: '', // Optional procedure reference
  // FT-116 REWRITE: assignedVessels replaces scope toggle
  // Empty array means "current vessel" (set during save)
  assignedVessels: [],

  // Step 2-4: Hazards (populated dynamically)
  hazards: [],

  // Step 5: Review metadata
  reviewFrequency: 'annually',
  createdBy: null
});

/**
 * RiskAssessmentForm Component
 * @param {Object} props - Component props
 * @param {Object} props.assessment - Existing assessment to edit (null for new)
 * @param {Function} props.onSave - Callback when assessment is saved
 * @param {Function} props.onBack - Callback to return to main view
 * @param {string} props.vesselId - Current vessel ID
 */
export const RiskAssessmentForm = ({
  assessment = null,
  onSave,
  onBack,
  vesselId = null
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(getInitialFormData);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showSavedNotification, setShowSavedNotification] = useState(false);
  const autoSaveTimer = useRef(null);

  // FT-116 REWRITE: All RAs go to fleet storage
  const { add: addFleetRA, update: updateFleetRA } = useFleetRiskAssessments();

  // Load assessment data for editing
  useEffect(() => {
    if (assessment) {
      setFormData({
        title: assessment.title || '',
        type: assessment.type || 'generic',
        description: assessment.description || '',
        selectedTask: assessment.selectedTask || null,
        location: assessment.location || '',
        department: assessment.department || '',
        hazards: assessment.hazards || [],
        reviewFrequency: assessment.reviewFrequency || 'annually',
        createdBy: assessment.createdBy || null,
        // FT-116 REWRITE: Load assignedVessels directly
        assignedVessels: assessment.assignedVessels || []
      });
    }
  }, [assessment]);

  /**
   * Auto-save to localStorage (for crash recovery)
   * This is NOT the same as saving to database
   * NOTE: Must be defined BEFORE the useEffect that uses it
   */
  const autoSaveToLocalStorage = useCallback(() => {
    try {
      const draftKey = assessment
        ? `ra_draft_${assessment.id}`
        : `ra_draft_new_${vesselId || 'default'}`;

      localStorage.setItem(draftKey, JSON.stringify({
        formData,
        savedAt: new Date().toISOString()
      }));
    } catch (e) {
      console.warn('Failed to auto-save:', e);
    }
  }, [formData, assessment, vesselId]);

  // Auto-save to localStorage every 30 seconds (for crash recovery)
  useEffect(() => {
    autoSaveTimer.current = setInterval(() => {
      if (formData.title && formData.hazards.length > 0) {
        autoSaveToLocalStorage();
      }
    }, 30000);

    return () => {
      if (autoSaveTimer.current) {
        clearInterval(autoSaveTimer.current);
      }
    };
  }, [formData, autoSaveToLocalStorage]);

  /**
   * Save as draft to database (not just localStorage)
   * This saves an incomplete assessment that user can return to later
   */
  const handleSaveAsDraft = useCallback(async () => {
    // Minimal validation - just need a title
    if (!formData.title || formData.title.trim().length < 3) {
      setErrors({ title: 'Please enter a title to save as draft' });
      return;
    }

    setIsSaving(true);

    try {
      const hazardInstances = formData.hazards.map(h => new Hazard(h));

      const assessmentData = {
        title: formData.title,
        type: formData.type || 'generic',
        description: formData.description || '',
        location: formData.location || '',
        department: formData.department || '',
        selectedTask: formData.selectedTask,
        hazards: hazardInstances,
        reviewFrequency: formData.reviewFrequency || 'annually',
        procedureNotes: formData.procedureNotes || '',
        status: assessment?.status || 'draft',  // Preserve existing, default to draft for new
        tier: 'lite',
        // Mark as incomplete so we know to prompt user
        isComplete: false,
        lastEditedStep: currentStep
      };

      let savedAssessment;

      // FT-116 REWRITE: Always save to fleet storage
      const assignedVessels = formData.assignedVessels?.length > 0
        ? formData.assignedVessels
        : [vesselId];

      if (assessment) {
        // Update existing draft
        await updateFleetRA(assessment.id, {
          ...assessmentData,
          assignedVessels,
          homeVessel: assessment.homeVessel || vesselId
        });
        savedAssessment = { ...assessment, ...assessmentData, assignedVessels };
      } else {
        // Create new draft - always goes to fleet storage
        savedAssessment = addFleetRA({
          ...assessmentData,
          homeVessel: vesselId,
          assignedVessels
        });
      }

      // Clear localStorage draft
      const draftKey = assessment
        ? `ra_draft_${assessment.id}`
        : `ra_draft_new_${vesselId || 'default'}`;
      localStorage.removeItem(draftKey);

      // Show success and close
      onSave?.(savedAssessment);

    } catch (error) {
      console.error('Failed to save draft:', error);
      setErrors({ submit: error.message || 'Failed to save draft' });
    } finally {
      setIsSaving(false);
    }
  }, [formData, assessment, vesselId, currentStep, addFleetRA, updateFleetRA, onSave]);

  /**
   * Validate current step
   */
  const validateStep = useCallback((step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.title || formData.title.trim().length < 5) {
          newErrors.title = 'Title must be at least 5 characters';
        }
        break;

      case 2:
        if (!formData.hazards || formData.hazards.length === 0) {
          newErrors.hazards = 'At least one hazard must be identified';
        } else {
          formData.hazards.forEach((hazard, index) => {
            if (!hazard.hazardDescription || hazard.hazardDescription.trim() === '') {
              newErrors[`hazard_${index}`] = 'Hazard description is required';
            }
          });
        }
        break;

      case 3:
        // Scoring is optional but hazards should have valid scores
        break;

      case 4:
        // Controls are optional but recommended
        break;

      case 5:
        // Final validation before submission
        if (!formData.title || formData.title.trim().length < 5) {
          newErrors.title = 'Title is required';
        }
        if (!formData.hazards || formData.hazards.length === 0) {
          newErrors.hazards = 'At least one hazard is required';
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Navigate to next step
   */
  const nextStep = useCallback(() => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  }, [currentStep, validateStep]);

  /**
   * Navigate to previous step
   */
  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setErrors({});
  }, []);

  /**
   * Navigate to specific step
   */
  const goToStep = useCallback((step) => {
    if (step >= 1 && step <= 5) {
      setCurrentStep(step);
      setErrors({});
    }
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async () => {
    if (!validateStep(5)) {
      return;
    }

    setIsSaving(true);

    try {
      // Convert hazards to Hazard instances
      const hazardInstances = formData.hazards.map(h => new Hazard(h));

      // Create assessment data
      // FIXED: Preserve existing status when editing, default to 'active' for completed assessments
      const assessmentData = {
        title: formData.title,
        type: formData.type,
        description: formData.description,
        location: formData.location,
        department: formData.department,
        selectedTask: formData.selectedTask,
        hazards: hazardInstances,
        reviewFrequency: formData.reviewFrequency,
        procedureNotes: formData.procedureNotes || '',
        status: assessment?.status || 'active',  // New assessments are 'active' when completed
        tier: assessment?.tier || 'lite',
        isComplete: true  // Mark as complete since all steps were finished
      };

      let savedAssessment;

      // FT-116 REWRITE: Always save to fleet storage
      const assignedVessels = formData.assignedVessels?.length > 0
        ? formData.assignedVessels
        : [vesselId];

      if (assessment) {
        // Update existing
        await updateFleetRA(assessment.id, {
          ...assessmentData,
          assignedVessels,
          homeVessel: assessment.homeVessel || vesselId
        });
        savedAssessment = { ...assessment, ...assessmentData, assignedVessels };
      } else {
        // Create new - always goes to fleet storage
        savedAssessment = addFleetRA({
          ...assessmentData,
          homeVessel: vesselId,
          assignedVessels
        });
      }

      // Clear draft
      const draftKey = assessment
        ? `ra_draft_${assessment.id}`
        : `ra_draft_new_${vesselId || 'default'}`;
      localStorage.removeItem(draftKey);

      // Notify parent
      onSave?.(savedAssessment);

    } catch (error) {
      console.error('Failed to save assessment:', error);
      setErrors({ submit: error.message || 'Failed to save assessment' });
    } finally {
      setIsSaving(false);
    }
  }, [formData, assessment, vesselId, addFleetRA, updateFleetRA, validateStep, onSave]);

  // FT-501: Mobile bottom bar navigation (replaces fixed footer on small screens)
  useSetBottomBarActions([
    {
      key: 'ra-prev',
      icon: ChevronLeft,
      label: 'Back',
      disabled: currentStep === 1,
      onClick: prevStep,
    },
    {
      // FT-501 fix: step-dependent key so the bottom-bar shapeKey changes at
      // step 5 and the bar re-pushes — otherwise the Next->Submit label/icon
      // swap (which shapeKey excludes) would render stale on mobile.
      key: currentStep < 5 ? 'ra-next' : 'ra-submit',
      icon: currentStep < 5 ? ChevronRight : CheckCircle,
      label: currentStep < 5 ? 'Next' : 'Submit',
      primary: true,
      // Parity with the desktop Save button (disabled={isSaving}); also guards
      // against a double-tap firing handleSubmit twice on mobile.
      disabled: currentStep === 5 && isSaving,
      onClick: currentStep < 5 ? nextStep : handleSubmit,
    },
  ]);

  /**
   * Render current step content
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1_TaskDetails
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <Step2_HazardIdentification
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
        );
      case 3:
        return (
          <Step3_RiskScoring
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 4:
        return (
          <Step4_ControlMeasures
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 5:
        return (
          <Step5_Review
            formData={formData}
            setFormData={setFormData}
            goToStep={goToStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4">
          {/* Back Button */}
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-3 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to Assessments
          </button>

          {/* Title Row */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {assessment ? 'Edit Risk Assessment' : 'Create Risk Assessment'}
              </h1>
              {formData.title && (
                <p className="text-sm text-gray-500 mt-0.5">{formData.title}</p>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Auto-save notification */}
              {showSavedNotification && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  Draft saved
                </span>
              )}

              {/* Save as draft and exit button */}
              <button
                onClick={handleSaveAsDraft}
                disabled={isSaving}
                className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                title="Save as draft and exit"
              >
                <Save className="h-5 w-5" />
                <span className="text-sm">Save & Exit</span>
              </button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="mt-6 flex justify-between relative">
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200" />
            {STEPS.map((step) => (
              <div key={step.id} className="relative z-10 flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                    currentStep === step.id
                      ? 'bg-cyan-600 text-white'
                      : currentStep > step.id
                      ? 'bg-green-500 text-white'
                      : 'bg-white border-2 border-gray-300'
                  }`}
                >
                  {step.icon}
                </div>
                <span className="mt-2 text-xs text-center max-w-[60px] text-gray-600">
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Step {currentStep}: {STEPS[currentStep - 1].label}
          </h3>
          {renderStepContent()}

          {/* Submission Error */}
          {errors.submit && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Failed to save</p>
                <p className="text-sm text-red-700">{errors.submit}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Navigation - Desktop only; mobile uses shell bottom bar (FT-501) */}
      <div className="hidden lg:block fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* Previous button */}
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className={`px-4 py-2 rounded-md flex items-center ${
              currentStep === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </button>

          {/* Step indicator */}
          <span className="text-sm text-gray-500">
            Step {currentStep} of 5
          </span>

          {/* Next/Submit button */}
          {currentStep < 5 ? (
            <button
              onClick={nextStep}
              className="px-6 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700 flex items-center transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className={`px-6 py-2 rounded-md flex items-center transition-colors ${
                isSaving
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Assessment
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Spacer for fixed footer - desktop only (FT-501) */}
      <div className="hidden lg:block h-20" />
    </div>
  );
};

export default RiskAssessmentForm;
